/**
 * tekrevive.js — shared utility runtime for TekRevive theme.
 *
 * Exposed on `window.tk`:
 *   - initTypewriter(el, words, opts)   cycle through words in `el` with typing animation
 *   - initScrollReveal(opts)            add .is-revealed when [data-reveal] enters viewport
 *   - initCountUp(opts)                 animate [data-count-to] numbers from 0 → target
 *   - fmt.money(n, currency='AUD')      format integer/decimal as currency
 *   - fmt.compactNumber(n)              50000 → "50k"
 *   - deviceThumbSVG(type, brand, size) return <svg> string for a device thumbnail
 *   - debounce(fn, ms)                  debounce helper
 *
 * Auto-runs initScrollReveal + initCountUp on DOMContentLoaded.
 */
(function () {
  "use strict";

  const tk = {};

  /* ---------- formatters ---------- */
  const audFmt = new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    maximumFractionDigits: 0,
  });
  tk.fmt = {
    money(n, _currency) {
      if (typeof n !== "number" || isNaN(n)) return "—";
      return audFmt.format(n);
    },
    compactNumber(n) {
      if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
      if (n >= 1_000) return (n / 1_000).toFixed(1).replace(/\.0$/, "") + "k";
      return String(n);
    },
  };

  /* ---------- debounce ---------- */
  tk.debounce = function (fn, ms) {
    let t;
    return function () {
      const ctx = this;
      const args = arguments;
      clearTimeout(t);
      t = setTimeout(() => fn.apply(ctx, args), ms);
    };
  };

  /* ---------- typewriter ---------- */
  tk.initTypewriter = function (el, words, opts) {
    if (!el || !Array.isArray(words) || !words.length) return;
    const cfg = Object.assign({ typeMs: 75, eraseMs: 40, holdMs: 1400 }, opts || {});
    let i = 0;
    function cycle() {
      const word = words[i % words.length];
      let pos = 0;
      el.textContent = "";
      const type = setInterval(() => {
        el.textContent = word.slice(0, ++pos);
        if (pos === word.length) {
          clearInterval(type);
          setTimeout(erase, cfg.holdMs);
        }
      }, cfg.typeMs);
      function erase() {
        const rm = setInterval(() => {
          el.textContent = word.slice(0, --pos);
          if (pos === 0) {
            clearInterval(rm);
            i++;
            setTimeout(cycle, 200);
          }
        }, cfg.eraseMs);
      }
    }
    cycle();
  };

  /* ---------- Shared IntersectionObserver registry ----------
   * Single pool of IO instances keyed by config string. Callbacks attached
   * via WeakMap so GC reclaims when element is removed from DOM.
   * Public API:
   *   tk.observe(el, fn, opts?) — fn(entry, el) on intersect. opts: { threshold, rootMargin, once=true }.
   *   tk.observeAll(selectorOrEls, fn, opts?) — sugar for batch.
   * Falls back to immediate-fire if IntersectionObserver unsupported.
   */
  const ioPool = new Map();      // configKey -> IntersectionObserver
  const ioCallbacks = new WeakMap(); // element -> { fn, once }
  function ioKey(opts) {
    const t = Array.isArray(opts.threshold) ? opts.threshold.join(",") : String(opts.threshold);
    return t + "|" + opts.rootMargin;
  }
  function getObserver(opts) {
    const key = ioKey(opts);
    let io = ioPool.get(key);
    if (io) return io;
    io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          const meta = ioCallbacks.get(e.target);
          if (!meta) continue;
          if (e.isIntersecting) {
            meta.fn(e, e.target);
            if (meta.once !== false) io.unobserve(e.target);
          }
        }
      },
      { threshold: opts.threshold, rootMargin: opts.rootMargin },
    );
    ioPool.set(key, io);
    return io;
  }
  tk.observe = function (el, fn, opts) {
    if (!el || typeof fn !== "function") return;
    const cfg = Object.assign({ threshold: 0.1, rootMargin: "0px 0px -10% 0px", once: true }, opts || {});
    if (!("IntersectionObserver" in window)) { fn({ isIntersecting: true, target: el }, el); return; }
    ioCallbacks.set(el, { fn, once: cfg.once });
    getObserver(cfg).observe(el);
  };
  tk.observeAll = function (selectorOrEls, fn, opts) {
    const els = typeof selectorOrEls === "string"
      ? document.querySelectorAll(selectorOrEls)
      : selectorOrEls;
    els.forEach((el) => tk.observe(el, fn, opts));
  };

  /* ---------- scrollReveal (uses tk.observe) ---------- */
  tk.initScrollReveal = function (opts) {
    const cfg = Object.assign({ selector: "[data-reveal]", revealClass: "is-revealed" }, opts || {});
    const els = document.querySelectorAll(cfg.selector);
    if (!els.length) return;
    if (!("IntersectionObserver" in window)) {
      els.forEach((el) => el.classList.add(cfg.revealClass));
      return;
    }
    tk.observeAll(els, (_e, el) => el.classList.add(cfg.revealClass), { threshold: 0.1, rootMargin: "0px 0px -10% 0px" });
  };

  /* ---------- countUp ---------- */
  tk.initCountUp = function (opts) {
    const cfg = Object.assign({ selector: "[data-count-to]", durationMs: 1400 }, opts || {});
    const els = document.querySelectorAll(cfg.selector);
    if (!els.length) return;
    function format(value, el) {
      const prefix = el.getAttribute("data-count-prefix") || "";
      const suffix = el.getAttribute("data-count-suffix") || "";
      const compact = el.hasAttribute("data-count-compact");
      const display = compact ? tk.fmt.compactNumber(Math.round(value)) : Math.round(value).toLocaleString();
      return prefix + display + suffix;
    }
    function animate(el) {
      if (el.dataset.cuDone === "1") return;
      el.dataset.cuDone = "1";
      const to = parseFloat(el.getAttribute("data-count-to")) || 0;
      const start = performance.now();
      function tick(t) {
        const p = Math.min(1, (t - start) / cfg.durationMs);
        const eased = 1 - Math.pow(1 - p, 3);
        el.textContent = format(to * eased, el);
        if (p < 1) requestAnimationFrame(tick);
      }
      requestAnimationFrame(tick);
    }
    /* Already-visible elements (hero stats — above the fold): fire immediately. */
    els.forEach((el) => {
      const r = el.getBoundingClientRect();
      const inView = r.top < window.innerHeight && r.bottom > 0;
      if (inView) animate(el);
    });
    if (!("IntersectionObserver" in window)) {
      els.forEach(animate);
      return;
    }
    /* Trigger as soon as 1px enters viewport — works for short bands (stats-band)
       as well as tall sections (hero). */
    tk.observeAll(els, (_e, el) => animate(el), { threshold: 0, rootMargin: "0px 0px -5% 0px" });
  };

  /* ---------- deviceThumbSVG ---------- */
  tk.deviceThumbSVG = function (type, brand, size) {
    const s = size || 64;
    const fill = "currentColor";
    if (type === "phone") {
      return `<svg width="${s}" height="${s}" viewBox="0 0 64 64" fill="none" aria-hidden="true"><rect x="18" y="6" width="28" height="52" rx="4" stroke="${fill}" stroke-width="2.4"/><rect x="22" y="10" width="20" height="40" rx="1" fill="${fill}" opacity=".08"/><circle cx="32" cy="54" r="1.6" fill="${fill}"/></svg>`;
    }
    if (type === "tablet") {
      return `<svg width="${s}" height="${s}" viewBox="0 0 64 64" fill="none" aria-hidden="true"><rect x="8" y="10" width="48" height="44" rx="4" stroke="${fill}" stroke-width="2.4"/><rect x="12" y="14" width="40" height="32" rx="1" fill="${fill}" opacity=".08"/><circle cx="32" cy="50" r="1.6" fill="${fill}"/></svg>`;
    }
    if (type === "laptop") {
      return `<svg width="${s}" height="${s}" viewBox="0 0 64 64" fill="none" aria-hidden="true"><rect x="10" y="12" width="44" height="30" rx="2" stroke="${fill}" stroke-width="2.4"/><rect x="4" y="44" width="56" height="6" rx="2" stroke="${fill}" stroke-width="2.4"/></svg>`;
    }
    if (type === "watch") {
      return `<svg width="${s}" height="${s}" viewBox="0 0 64 64" fill="none" aria-hidden="true"><rect x="20" y="20" width="24" height="24" rx="5" stroke="${fill}" stroke-width="2.4"/><rect x="24" y="10" width="16" height="12" rx="2" fill="${fill}" opacity=".5"/><rect x="24" y="42" width="16" height="12" rx="2" fill="${fill}" opacity=".5"/></svg>`;
    }
    return `<svg width="${s}" height="${s}" viewBox="0 0 64 64" aria-hidden="true"><rect x="14" y="14" width="36" height="36" rx="4" stroke="${fill}" stroke-width="2.4" fill="none"/></svg>`;
  };

  /* ---------- error boundary ---------- */
  tk.initErrorBoundary = function () {
    function toast(msg) {
      let host = document.getElementById("tk-toast-host");
      if (!host) {
        host = document.createElement("div");
        host.id = "tk-toast-host";
        host.style.cssText = "position:fixed;bottom:20px;left:50%;transform:translateX(-50%);z-index:9999;display:flex;flex-direction:column;gap:8px;";
        document.body.appendChild(host);
      }
      const t = document.createElement("div");
      t.className = "tk-toast";
      t.style.cssText = "background:#1F252D;color:#fff;padding:10px 16px;border-radius:10px;font-size:13px;box-shadow:0 8px 24px rgba(0,0,0,.15);max-width:320px;";
      t.textContent = msg;
      host.appendChild(t);
      setTimeout(() => t.remove(), 5000);
    }
    function report(err, kind) {
      try {
        const payload = {
          kind, msg: String(err && err.message || err), stack: err && err.stack,
          url: location.href, ua: navigator.userAgent, ts: Date.now(),
        };
        if (navigator.sendBeacon) {
          navigator.sendBeacon("/apps/tekrevive-error", JSON.stringify(payload));
        }
      } catch (e) {}
    }
    window.addEventListener("error", (e) => { report(e.error || e, "error"); });
    window.addEventListener("unhandledrejection", (e) => { report(e.reason, "promise"); });
    tk.toast = toast;
  };

  /* ---------- auto-init ---------- */
  function ready() {
    tk.initErrorBoundary();
    tk.initScrollReveal();
    tk.initCountUp();
    document.querySelectorAll("[data-typewriter]").forEach((el) => {
      const words = (el.getAttribute("data-typewriter") || "").split("|").map((w) => w.trim()).filter(Boolean);
      if (!words.length) return;
      // Pick one word at random per page load and show it static — no cycling
      // (the running typewriter was visually fatiguing). A single soft fade-in on load.
      const pick = words[Math.floor(Math.random() * words.length)];
      el.textContent = pick;
      if (!matchMedia("(prefers-reduced-motion: reduce)").matches) {
        el.style.opacity = "0";
        el.style.transition = "opacity .6s ease";
        requestAnimationFrame(() => requestAnimationFrame(() => { el.style.opacity = "1"; }));
      }
    });
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", ready);
  } else {
    ready();
  }

  window.tk = tk;
})();

/* Perf: pause infinite CSS animations in off-screen sections (Intel/integrated GPUs
   keep compositing paused layers otherwise). Adds .tk-offscreen when a section
   leaves the viewport, removes it when near. Idle-deferred, no-op if unsupported. */
(function () {
  function initOffscreenAnim() {
    if (!('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        entries[i].target.classList.toggle('tk-offscreen', !entries[i].isIntersecting);
      }
    }, { rootMargin: '200px 0px' });
    var sections = document.querySelectorAll('.shopify-section');
    for (var i = 0; i < sections.length; i++) io.observe(sections[i]);
  }
  if (document.readyState === 'complete') {
    ('requestIdleCallback' in window) ? requestIdleCallback(initOffscreenAnim, { timeout: 1500 }) : setTimeout(initOffscreenAnim, 50);
  } else {
    window.addEventListener('load', function () {
      ('requestIdleCallback' in window) ? requestIdleCallback(initOffscreenAnim, { timeout: 1500 }) : setTimeout(initOffscreenAnim, 50);
    }, { once: true });
  }
})();

/* Perf/a11y: don't autoplay-decode hero/background video for reduced-motion users
   (heavy on integrated GPUs). Pause + show the poster; they can still click play. */
(function () {
  try {
    if (!window.matchMedia || !matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    var vids = document.querySelectorAll('video[autoplay]');
    for (var i = 0; i < vids.length; i++) {
      vids[i].removeAttribute('autoplay');
      vids[i].setAttribute('controls', '');
      try { vids[i].pause(); } catch (e) {}
    }
  } catch (e) {}
})();
